/* Copyright (c) 2020-present CloudIO Inc */

import { isNotEmpty } from 'utils/isEmpty';
import { Platform } from '../../../../platform/Platform';
import {
  AliasType,
  ComponentManager,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { baseColumnProps, isBaseColumnPropertyVisible } from '../BaseColumn';
import { defaultValue } from './LOVColumn';
import { analyzeLOVColumn } from './LOVColumnAnalyze';

const LOVColumnManager: ComponentManagerFn<'LOVColumn'> = (
  platform: Platform,
): ComponentManager<'LOVColumn'> => {
  return {
    ...baseComponentManager<'LOVColumn'>(platform, 'LOVColumn'),
    type: 'LOVColumn',
    name: 'LOV Column',
    isChildAllowed: (page, item, childType, { appUid, pageId }) => {
      if ('ColumnFooter' === childType) {
        return true;
      }
      if (
        !(
          [
            'LOVGrid',
            'LOVMappings',
            'TriggerOnCellClick',
            'TriggerOnChange',
            'TriggerAfterServerValidate',
          ] as ItemType[]
        ).includes(childType)
      ) {
        return false;
      }
      const children = platform.getChildrenOfType(
        appUid,
        pageId,
        page,
        item,
        childType,
      );
      return children.length === 0 ? true : 'Already added';
    },
    defaultValue,
    defaultChildren: ['LOVGrid'],
    props: {
      ...baseColumnProps,
      viewAttribute: {
        ...(baseColumnProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: ({ props }) => ['String'],
      },
      comboDatasourceAlias: {
        label: 'Data Store Alias',
        type: 'DataSourceAliasChooser',
        category: 'Data',
        allowedTypes: ['ReadOnlyDataStore'],
        required: true,
      },
      doNotPrefetch: {
        label: 'Do Not Prefetch',
        type: 'boolean',
        category: 'Data',
        helpText: 'Enable this to prevent blank query on LOV open',
      },
      returnValueAttribute: {
        label: 'Return Value Attribute',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        category: 'Data',
        required: true,
        getAttributeDataTypes: () => 'ALL',
      },
      open: {
        label: 'Open',
        type: 'boolean',
      },
      popupWidth: {
        label: 'Popup Width',
        type: 'number',
        min: 400,
        max: 1200,
        step: 40,
      },
      popupHeight: {
        label: 'Popup Height',
        type: 'number',
        min: 200,
        max: 800,
        step: 40,
      },
      showGridFilters: {
        label: 'Show Grid Filters',
        type: 'boolean',
      },
      showLovIcon: {
        label: 'Show LOV Icon',
        type: 'boolean',
        helpText:
          'When enabled, the LOV icon is always visible if the field is editable. When disabled, it appears only when the field is focused.',
      },
      serverValidateOnChange: {
        category: 'Validation',
        label: 'Server Validate On Change',
        type: 'boolean',
      },
      allowInvalidValue: {
        label: 'Allow Invalid Value',
        type: 'boolean',
        category: 'Data',
      },
      multiSelect: {
        label: 'Enable Multi-Select',
        type: 'boolean',
        category: 'Data',
        helpText:
          'Allow selecting multiple values and creating rows for each selection.',
      },
      ignoreSpecialCharacters: {
        label: 'Ignore Special Characters',
        type: 'boolean',
        helpText: 'Enable this to ignore special characters while searching.',
        category: 'Data',
      },
      specialCharacters: {
        label: 'Special Characters',
        type: 'string',
        helpText:
          'Enter special characters to exclude during the search. Use a semicolon (;) as the delimiter.',
        category: 'Data',
      },
      defaultFilterFocusColumn: {
        label: 'Default Filter Focus Column',
        type: 'ItemID',
        helpText:
          'Controls default filter focus. If a column Id is provided, the corresponding filter input will be focused when the popup opens',
        initialFilter: 'LOVGrid',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          const parentItem = platform.getItem(
            appUid,
            pageId,
            item?.parent || '',
          );

          if (parentItem?.type !== 'LOVGrid') {
            return `Item id '${value}' doesn't belong to a Grid`;
          }
          return '';
        },
      },
      popupCssClassName: {
        label: 'Lov Popup CSS Class Name',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Look & Feel',
      },
    },
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV' ? { aliasProp: 'comboDatasourceAlias' } : undefined,
    isDataSourceAliasUsed: (item, alias) =>
      item.props.comboDatasourceAlias === alias,
    isPropertyVisible(prop, itemData, { appUid, itemId, pageId }): boolean {
      if (
        itemId &&
        !isBaseColumnPropertyVisible({ appUid, platform, itemId, pageId, prop })
      ) {
        return false;
      }
      switch (prop) {
        case 'returnValueAttribute':
          return isNotEmpty(itemData.comboDatasourceAlias);
        case 'open':
          return false;
        case 'specialCharacters':
          return !!itemData.ignoreSpecialCharacters;
        default:
          return true;
      }
    },
    generateShortId: (item) =>
      typeof item.label === 'string' ? item.label : item.label?.value,
    getDisplayText: (item) =>
      (typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label?.value) ?? '',
    analyze: analyzeLOVColumn,
    category: 'Data Grid',
    icon: { iconName: 'io-lov-field', iconType: 'cloudio' },
    excludeChildren: true,
    description: `**Overview**<br/>
The LOV Column (List of Values) is an advanced data entry column used for selecting records from large or complex datasets.
Unlike a Select Column (Dropdown), which only shows a simple list of text labels, the LOV Column opens a Modal Popup containing a full Data Grid. This allows users to search, filter, and view multiple columns of information (e.g., "Customer Name," "City," "Account ID") before making a selection. It is essential for "Master Data" lookups like picking a Customer, Product, or GL Account.

**1. Data Binding**<br/>
This column connects two datasets: the row you are editing and the reference list you are picking from.
* **View Attribute:** The field in your main Data Store where the selected value will be saved (e.g., "customer_id").
* **Data Store Alias:** The external ReadOnly Data Store that populates the popup list.
* **Return Value Attribute:** The specific field from the selected record in the popup that maps back to the View Attribute.
* **Example:** You might see "Amazon" and "Google" in the list, but when you select one, the system saves their VendorID (e.g., V-1001) to your main table.

**2. Designing the Popup (The LOV Grid)**<br/>
The content of the popup is fully configurable because it uses a standard grid structure.
* **Child Component (LOV Grid):** The LOV Column must contain an **LOV Grid** child. This child acts exactly like a regular Data Grid—you add columns (String, Number, Date) to it to define what the user sees inside the popup. (Default: When you add an LOV Column, an LOVGrid child is automatically created).
* **Dimensions:**
    * **Popup Width / Height:** Controls the size of the modal window (e.g., 800 x 600). Adjust this based on how many columns you need to show.
* **Show Grid Filters:** Enables the standard filter row inside the popup, allowing users to search specific columns (e.g., "Filter City by 'New York'").

**3. Advanced Data Mapping**<br/>
A powerful feature of the LOV Column is its ability to fill multiple fields at once.
* **Child Component (LOV Mappings):** Purpose: When a user selects a record (e.g., "Product A"), you can automatically copy other fields from that product (e.g., "Unit Price", "Description", "Tax Code") into other columns in the same row.
* **Benefit:** Reduces manual data entry and ensures consistency.

**4. Behavior & Performance**<br/>
* **Do Not Prefetch:**
    * **Enabled:** The system waits to fetch data until the user explicitly searches or opens the popup. Best for massive datasets to save bandwidth.
    * **Disabled:** The data loads immediately.
* **Allow Invalid Value:** If enabled, users can type text that doesn't exist in the list.
* **Show LOV Icon:** Controls visibility of the "Search/Magnifying Glass" icon inside the cell (Always Visible vs. Only on Focus).

**5. Comparison: Select vs. LOV**<br/>
* **UI:** Select is a Simple Dropdown; LOV is a Modal Grid Popup.
* **Information:** Select shows a Single Label; LOV shows Multiple Columns.
* **Filtering:** Select has Basic Text Match; LOV has Advanced Grid Filtering.
* **Dataset Size:** Select is for Small/Medium (< 1k); LOV is for Large/Massive (10k+).
* **Mappings:** Select does Simple ID-to-Label; LOV does Multi-Column Return Mapping.`,
  };
};

export default LOVColumnManager;
